import { DateTime } from 'luxon';
import { PG_FRACTION_DECIMALS } from './constants';

/** Parses a user-typed decimal (`3,5` or `3.5`); empty or invalid — `null`. */
export const parseDecimal = (value: string | number | null | undefined): number | null => {
	if (value === null || value === undefined) return null;
	const text = String(value).trim().replace(',', '.');
	if (text === '' || !/^-?\d*(\.\d*)?$/.test(text) || text === '-' || text === '.') return null;
	const parsed = Number(text);
	return Number.isFinite(parsed) ? parsed : null;
};

/** Number of digits after the decimal point of a typed value. */
export const countDecimals = (value: string | number): number => {
	const text = String(value).trim().replace(',', '.');
	const dot = text.indexOf('.');
	return dot < 0 ? 0 : text.length - dot - 1;
};

/** Rounds half away from zero to `digits` decimals, tolerant to binary noise (`1.005` → `1.01`). */
export const roundTo = (value: number, digits: number): number => {
	const factor = 10 ** digits;
	const shifted = Math.abs(value) * factor;
	const rounded = Math.round(Number(shifted.toPrecision(15)));
	return (Math.sign(value) * rounded) / factor;
};

/** Rounds up (towards +infinity for positive amounts) to cents, like `MoneyMath.RoundUp2`. */
export const roundUp2 = (value: number): number => {
	const shifted = Number((value * 100).toPrecision(15));
	return Math.ceil(shifted) / 100;
};

/** Fraction of the gateway (`0.03`) as a percent string for an input (`3`). */
export const fractionToPercentInput = (fraction: number | null | undefined): string => {
	if (fraction === null || fraction === undefined) return '';
	return String(roundTo(fraction * 100, PG_FRACTION_DECIMALS - 2));
};

/** Percent typed by the admin (`3`) as the fraction the gateway stores (`0.03`). */
export const percentInputToFraction = (value: string): number | null => {
	const percent = parseDecimal(value);
	return percent === null ? null : roundTo(percent / 100, PG_FRACTION_DECIMALS);
};

/** `0.03` → `3 %`. */
export const formatPercent = (fraction: number | null | undefined): string =>
	fraction === null || fraction === undefined ? '—' : `${fractionToPercentInput(fraction)} %`;

/** `12.5` → `12.50`, with an optional currency: `12.50 EUR`. */
export const formatMoney = (
	amount: number | null | undefined,
	currency?: string | null,
): string => {
	if (amount === null || amount === undefined) return '—';
	const text = amount.toLocaleString('en-GB', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
	return currency ? `${text} ${currency}` : text;
};

/** Date-time of the gateway (ISO with offset) in the operator's local time. */
export const formatDateTime = (value: string | null | undefined): string => {
	if (!value) return '—';
	const parsed = DateTime.fromISO(value);
	return parsed.isValid ? parsed.toFormat('dd.MM.yyyy, HH:mm:ss') : value;
};

/** Plain date `YYYY-MM-DD` as `dd.MM.yyyy`. */
export const formatDate = (value: string | null | undefined): string => {
	if (!value) return '—';
	const parsed = DateTime.fromISO(value);
	return parsed.isValid ? parsed.toFormat('dd.MM.yyyy') : value;
};

/**
 * Gross-up fee as the gateway computes it (`PricingCalculator.QuoteFee`):
 * `fee = RoundUp2(base / (1 − p) − base) + fixed`, `total = base + fee`.
 */
export const grossUpFee = (
	baseAmount: number,
	percent: number,
	fixedAmount: number,
): { fee: number; total: number } => {
	const base = roundTo(baseAmount, 2);
	const percentPart = percent > 0 ? roundUp2(base / (1 - percent) - base) : 0;
	const fee = roundTo(percentPart + fixedAmount, 2);
	return { fee, total: roundTo(base + fee, 2) };
};

/** JSON for `<pre>` blocks (stats, details). */
export const toPrettyJson = (value: unknown): string => {
	try {
		return JSON.stringify(value ?? null, null, 2);
	} catch {
		return String(value);
	}
};
